import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import LnatEditorialFooter from './LnatEditorialFooter';
import './LnatLandingPage.css';

import { getBaseApiUrl, getEduPortalUrl } from '../../../config/api';
import { checkoutRefusalMessage, showCheckoutRefusal } from '../../checkout/checkoutRefusal';
import { ORG_PLAN_NOTICE, useStoredUserMayPurchase } from '../../checkout/purchaseEligibility';
import { EXAM_PACKAGES } from '../../../config/pricingCatalog';
import { consentAllows } from '../../consent/consentStore';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the admissions matrix, method cards, sample
// question and school case-study sections).
function ScrollCarousel({ children, ariaLabel, trackClassName }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="lnat-scroll-carousel">
      <button
        type="button"
        className={`lnat-carousel-arrow lnat-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`lnat-carousel-arrow lnat-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="lnat-card-scroll" ref={scrollRef}>
        <div className={`lnat-card-track ${trackClassName || ''}`}>
          {children}
        </div>
      </div>
      <div className="lnat-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function LnatLandingPage() {
  const navigate = useNavigate();
  const mayPurchase = useStoredUserMayPurchase();
  const [activeFaq, setActiveFaq] = useState(null);

  React.useEffect(() => {
    if (window.gtag && consentAllows('advertising')) {
      window.gtag('config', 'AW-18348840584', {
        page_title: 'LNAT Exam Prep 2026',
        page_location: window.location.href
      });
    }
  }, []);

  // Preserved from the previous implementation — currently unused in the
  // JSX below (they were unused in the prior version too), kept in place
  // in case they're wired up to a future free-practice entry point.
  const handleLeadSubmit = (e) => {
    e.preventDefault();
    const email = e.target.elements.email?.value;
    if (email) {
      sessionStorage.setItem('ivori_lead_email', email);
    }
    sessionStorage.setItem('ivori_lead_exam', 'LNAT');
    window.location.href = getEduPortalUrl('/practice/lnat');
  };

  const handleStartFreeSectionA = () => {
    window.location.href = getEduPortalUrl('/practice/lnat?section=A');
  };

  const handleStartFreeSectionB = () => {
    window.location.href = getEduPortalUrl('/practice/lnat?section=B');
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleExplorePlans = () => {
    const el = document.getElementById('pricing') || document.getElementById('lnat-pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubscribe = async (tierName) => {
    const tierSlug = tierName.toLowerCase().includes('beyond') ? 'lnat_beyond' : (tierName.toLowerCase().includes('foundation') ? 'lnat_foundations' : 'lnat_beginnings');

    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');
    const userRaw = localStorage.getItem('pathbuddy_user') || sessionStorage.getItem('pathbuddy_user') || localStorage.getItem('user');
    const user = (token && userRaw) ? (typeof userRaw === 'string' && userRaw.startsWith('{') ? JSON.parse(userRaw) : { email: userRaw }) : null;

    if (!user || !user.email) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'lnat');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=lnat`);
      return;
    }

    if (tierSlug === 'lnat_beginnings') {
      window.location.href = getEduPortalUrl('/practice/lnat');
      return;
    }

    try {
      const apiUrl = getBaseApiUrl();
      const token2 = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');

      const headers = { 'Content-Type': 'application/json' };
      if (token2) {
        headers['Authorization'] = `Bearer ${token2}`;
      }

      const res = await fetch(`${apiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tier_name: tierName,
          exam_type: 'lnat',
          user_id: user?.id,
          email: user?.email
        })
      });
      const data = await res.json().catch(() => ({}));
      const refusal = checkoutRefusalMessage(res, data);
      if (refusal) {
        showCheckoutRefusal(refusal);
        return;
      }
      if (data.checkoutUrl || data.url) {
        window.location.href = data.checkoutUrl || data.url;
      } else {
        sessionStorage.setItem('pending_checkout_tier', tierSlug);
        sessionStorage.setItem('pending_checkout_exam', 'lnat');
        navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=lnat`);
      }
    } catch (err) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'lnat');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=lnat`);
    }
  };

  const lnatRequirements = [
    { uni: "University of Oxford", course: "BA Law (Jurisprudence)", requirement: "Mandatory", sectionA: "28+ / 42", sectionB: "High Weight (Shortlisting)" },
    { uni: "University of Cambridge", course: "BA Law (Tripos)", requirement: "Mandatory", sectionA: "28+ / 42", sectionB: "High Weight" },
    { uni: "London School of Economics (LSE)", course: "LLB Law", requirement: "Mandatory", sectionA: "27+ / 42", sectionB: "Medium/High Weight" },
    { uni: "University College London (UCL)", course: "LLB Law", requirement: "Mandatory", sectionA: "27+ / 42", sectionB: "High Weight" },
    { uni: "King's College London (KCL)", course: "LLB Law", requirement: "Mandatory", sectionA: "27+ / 42", sectionB: "High Weight (Selection)" },
    { uni: "Durham University", course: "LLB Law", requirement: "Mandatory", sectionA: "26+ / 42", sectionB: "Medium Weight" },
    { uni: "University of Bristol", course: "LLB Law", requirement: "Mandatory", sectionA: "26+ / 42", sectionB: "Medium Weight" },
    { uni: "University of Glasgow", course: "LLB Law", requirement: "Mandatory", sectionA: "25+ / 42", sectionB: "Holistically Evaluated" },
    { uni: "University of Oxford", course: "Law with foundation year", requirement: "Mandatory", sectionA: "25+ / 42", sectionB: "High Weight (Shortlisting)" },
    { uni: "SOAS University of London", course: "LLB Law", requirement: "Mandatory", sectionA: "24+ / 42", sectionB: "Holistically Evaluated" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained to reason in line with the pedagogical traditions of the Oxford Tutorial and the Harvard Case Method, so it evaluates an argument the way a human tutor would",
        "Finds exactly where an argument stops working, so you correct the actual reasoning gap",
        <>The world's first <button type="button" className="lnat-inline-link lnat-link-btn" onClick={() => navigate('/method/lnat')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="lnat-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "One Method Two Traditions",
      title: "Oxford and Harvard",
      meta: "Trained on Both Deliberately",
      items: [
        "Multiple choice is a case, so you train the exact reasoning it tests, meaning the skill holds up on a case you've never practiced on",
        "An essay or an interview is a tutorial, so you learn to defend an argument, so your answer survives the follow-up question an examiner would ask",
        "Trains on the discipline both traditions were built to produce, targeting the reasoning skill being tested and what the assessment actually rewards"
      ]
    },
    {
      tag: "Tradition I",
      title: "Oxford Tutorial",
      meta: "Defend Your Argument",
      items: [
        "You take a position first, so you build the habit of committing to an argument before you know how it will be challenged",
        "Every follow-up probes the weakest sentence in your essay, exactly like a tutor would, spotting your own weak point before an examiner ever does",
        "You leave with an argument you can defend, not an opinion you happened to have, so the position holds up under scrutiny rather than just sounding confident"
      ]
    },
    {
      tag: "Tradition II",
      title: "Harvard Case Method",
      meta: "Extract the Principle",
      items: [
        "Every passage functions as a case, so the reasoning skill transfers to any exam or interview, not just the ones you've already seen",
        "Spot the one assumption an argument can't survive without, so you're practicing the actual mechanism the exam or the interview rewards, not a proxy for it",
        "Reasoning tested privately, one student at a time, so the rigor of the method comes without the anxiety of being cold-called in front of peers"
      ]
    },
    {
      tag: "Apply to LNAT Section A",
      title: "Multiple Choice",
      meta: "Answer 42 questions in 95 minutes",
      items: [
        "Every test or interview is treated as a case — so the skill transfers to assessments you've never seen, not just the ones you've drilled",
        "Every wrong answer is traced back to the exact reasoning gap that caused it, meaning the same mistake doesn't recur on assessment day",
        "Diagnostics show which underlying skill needs work, so revision closes the reasoning gap instead of re-drilling what you already know"
      ]
    },
    {
      tag: "Apply to LNAT Section B",
      title: "Essay",
      meta: "Write an essay in 40 minutes",
      items: [
        "Graded the way an Oxford tutor grades it — probing exactly where the argument breaks, so you learn to defend a position instead of just stating one",
        "Thesis, structure and counter-argument scored separately, so you know precisely which part of the essay to fix rather than guessing from one overall score number",
        "Feedback probes your weakest sentence specifically, so revision targets the actual point where the essay loses marks"
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Dom Bird · Deputy Head Teacher",
      body: "King Edward VI Handsworth Grammar School for Boys has used IVORI for almost two years, supporting Year 12 with admissions test preparation — including the LNAT — case study analysis, interview practice and UCAS personal statement support.",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Paul Mann · Head of Careers",
      body: "Piloted IVORI with Year 12 students transitioning into Year 13, preparing them for admissions tests and interviews across the full application cycle — with particular focus on the Oxbridge cohort sitting the LNAT.",
      detailLabel: "Cohort",
      detailValue: "Year 12 → Year 13",
      detail2Label: "Focus",
      detail2Value: "Oxbridge Admissions"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Building on the existing relationship with Handsworth Grammar, IVORI is in active discussions with the wider King Edward VI Foundation about extending LNAT preparation across its family of Birmingham grammar schools.",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "Deployed across schools within the grammar school group for LNAT practice, supporting Year 12 and 13 students preparing for law admissions tests and Oxbridge interviews.",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "LNAT Practice"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Deployed across schools within the independent school group for LNAT practice, supporting Year 12 and 13 students preparing for law admissions tests and Oxbridge interviews.",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "LNAT Practice"
    }
  ];

  const faqs = [
    {
      q: "Which law schools require the LNAT exam for 2026/2027 entry, and what does it test?",
      a: [
        "The LNAT is used by a number of leading UK universities as part of their law admissions process, including Oxford, Cambridge, LSE, UCL and KCL. Requirements vary by university and admissions cycle, so always check the university's current admissions requirements.",
        "No, the LNAT does not test knowledge of law. It is designed to assess skills such as reading comprehension, critical reasoning and argumentation rather than your knowledge of legal rules. You do not need to study law before taking the test."
      ]
    },
    {
      q: "What is IVORI's pedagogical method, and who's behind it?",
      a: [
        "Every passage, question and mark scheme is reviewed by law professionals and academics from top law faculties, alongside educators with formal pedagogical training. That review process is what lets IVORI explain not just the right answer, but why every alternative fails.",
        "IVORI draws on two teaching traditions built for reasoning under scrutiny: the Langdellian case method developed at Harvard Law School, and the Oxford tutorial model of close, dialectical questioning. Underneath both sits IVORI's own foundation decision world model — proprietary technology researched and pioneered in-house by IVORI Lab, and, we believe, the world's first pedagogical AI model built on this kind of foundation.",
        "It's IVORI Lab's proprietary foundation decision world model — a system trained to reason about decision-making under uncertainty, rather than simply predict text. IVORI's LNAT products sit on top of this underlying model, using its proprietary training methodology to evaluate arguments, weigh competing positions and generate the kind of structured feedback a human tutor would give."
      ]
    },
    {
      q: "How does IVORI's practice, grading and scoring work?",
      a: [
        "There is no universal \"pass mark\" for the LNAT. Competitive scores vary between universities, cohorts and admissions cycles. IVORI therefore uses preparation benchmarks rather than presenting a single score as a guaranteed admissions threshold.",
        "Submit your essay and IVORI evaluates the argument across key dimensions including thesis clarity, structure, reasoning, counterargument and written clarity. You receive structured feedback designed to help you understand what is working, what is weak and how to improve your argument.",
        "Full mocks reproduce the core timing structure of the LNAT, including the 95-minute Section A and 40-minute Section B. You can use timed practice to build both accuracy and exam-day pacing."
      ]
    },
    {
      q: "Can I use IVORI for more than just LNAT preparation?",
      a: ["Yes. IVORI's reasoning practice is designed around transferable skills including critical reading, argument analysis, identifying assumptions and constructing persuasive arguments. LNAT is one of several admissions preparation products within the IVORI ecosystem."]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes. All subscriptions can be managed or cancelled in one click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'lnat.ivoriedu.com';
  const onIvoriAiEdu = host === 'lnat.edu.ivoriai.com';
  const lnatUrl = onIvoriEdu
    ? 'https://lnat.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://lnat.edu.ivoriai.com/'
      : (host.includes('lnat') ? 'https://lnat.edu.ivoriai.com/' : 'https://ivoriai.com/edu/lnat');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="lnat-landing-root">
      <SeoMeta
        title="LNAT Exam Prep 2026 Mock Papers | ivori"
        description="Rank #1 for LNAT Prep 2026/2027. Practice Oxford, UCL, KCL & LSE Law admissions with computer mock test simulations, Section A reasoning drills, and instant Section B essay feedback."
        keywords="lnat prep 2026, lnat prep 2027, lnat prep uk, oxford lnat prep, ucl lnat prep, lnat exam prep, lnat practice test, lnat section a, lnat section b essay, oxford law test, ucl law admissions, lnat past papers, ivori lnat, lnat cut off scores, lnat score conversion, lnat essay marking, lnat past papers free, kcl law lnat, bristol lnat prep, lse law test, lnat simulator pearson vue"
        url={lnatUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "LNAT Law Entrance Examination Preparation",
                "description": "Prepare for the Law National Aptitude Test (LNAT) with guided practice questions, timed computer-based mock tests, Section A reasoning drills, and Section B essay feedback for Oxford, Cambridge, UCL, and LSE law admissions.",
                "provider": {
                  "@type": "Organization",
                  "name": "ivori",
                  "url": eduHomeUrl
                },
                "url": lnatUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "Section A Critical Verbal Reasoning & Deduction",
                  "Identifying Unstated Assumptions & Logical Fallacies",
                  "Evaluating Necessary vs. Sufficient Evidence",
                  "Section B Law Essay Argumentation & Structure"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": onIvoriEdu || onIvoriAiEdu ? eduHomeUrl : "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "LNAT Preparation", "item": lnatUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="exam-landing" />

      {/* HERO */}
      <section className="lnat-hero-section">
        <div className="lnat-container lnat-hero-content">
          <span className="lnat-mono-eyebrow">IVORI Edu</span>
          <h1 className="lnat-hero-title">Master the LNAT.</h1>
          <p className="lnat-hero-stat">80% of IVORI students score 27+ / 42.</p>
          <p className="lnat-hero-tagline">Train the thinking the LNAT rewards.</p>
          <button type="button" className="lnat-hero-cta" onClick={handleExplorePlans}>
            Choose Tier
          </button>
        </div>
      </section>

      {/* ADMISSIONS GUIDE */}
      <section id="lnat-matrix" className="lnat-editorial-section">
        <div className="lnat-container">
          <div className="lnat-editorial-section-head">
            <span className="lnat-mono-eyebrow">Target Scores</span>
            <h2>Craft Your Profile</h2>
            <p>Benchmark against Oxbridge and Russell Group university targets</p>
          </div>

          <ScrollCarousel ariaLabel="faculty">
            {lnatRequirements.map((row, idx) => (
              <div className="lnat-matrix-card" key={idx}>
                <div className="lnat-matrix-uni">{row.uni}</div>
                <div className="lnat-matrix-row"><span>Degree Program</span><b>{row.course}</b></div>
                <div className="lnat-matrix-row"><span>Requirement</span><b className="lnat-req-badge">{row.requirement}</b></div>
                <div className="lnat-matrix-row lnat-score-cell"><span>Section A Target</span><b>{row.sectionA}</b></div>
                <div className="lnat-matrix-row"><span>Section B Target</span><b>{row.sectionB}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* HOW IT WORKS / IVORI METHOD */}
      <section className="lnat-editorial-section">
        <div className="lnat-container">
          <div className="lnat-editorial-section-head">
            <span className="lnat-mono-eyebrow">How It Works</span>
            <h2>Utilize IVORI Edu</h2>
            <p>Learn through Oxford-style Tutorials and the Harvard Case Method</p>
          </div>

          <ScrollCarousel ariaLabel="section">
            {structureCards.map((card, idx) => (
              <div className="lnat-structure-card" key={idx}>
                <span className="lnat-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="lnat-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE QUESTION */}
      <section className="lnat-editorial-section">
        <div className="lnat-container">
          <div className="lnat-editorial-section-head">
            <span className="lnat-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how IVORI explains a Section A question and grades a Section B essay</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="lnat-qcard-panel">
              <span className="lnat-mono-tag">Section A</span>
              <div className="lnat-passage">
                "Proponents of algorithmic sentencing argue that machine learning models eliminate human judicial bias. However, because these algorithms are trained on historical arrest data from over-policed neighborhoods, they systematically replicate systemic societal biases under the guise of mathematical objectivity."
              </div>
              <h3>Which of the following represents an unstated assumption required for the author's counter-argument to hold?</h3>
            </div>

            <div className="lnat-qcard-panel">
              <div className="lnat-option">A) Human judges are entirely incapable of making unbiased sentencing decisions.</div>
              <div className="lnat-option correct">B) Historical arrest data reflects biased law enforcement patterns rather than purely objective crime distribution.</div>
              <div className="lnat-option">C) Algorithmic sentencing models should be immediately banned in all criminal courts.</div>
              <div className="lnat-option">D) Machine learning algorithms are incapable of updating their training weights over time.</div>
            </div>

            <div className="lnat-qcard-panel">
              <span className="lnat-mono-tag">Explanation</span>
              <p>The author's counter-argument claims the algorithm "replicates" bias — but this only follows if the training data itself encodes that bias.</p>
              <p>Option B supplies exactly this missing link: without it, the leap from "trained on historical arrest data" to "systematically replicate societal biases" doesn't hold.</p>
              <p>Options A, C and D go further than the passage supports, or aren't required for the logic to work.</p>
            </div>

            <div className="lnat-qcard-panel">
              <span className="lnat-mono-tag">Section B</span>
              <div className="lnat-option">Money can't buy happiness. Do you agree?</div>
              <div className="lnat-option">Is free speech more important than protection from harm?</div>
              <div className="lnat-option">Should there be a moral duty to rescue?</div>
            </div>

            <div className="lnat-qcard-panel">
              <span className="lnat-mono-tag">Student Essay Extract</span>
              <div className="lnat-passage">
                "Free speech must always take precedence over protection from harm, because without it, no other right can be meaningfully exercised or defended. A society that permits any restriction on speech, however narrow, opens the door to broader censorship, and history shows that once such restrictions exist, they are rarely rolled back. Therefore, free speech should be protected absolutely, regardless of the harm that specific instances of speech may cause."
              </div>
            </div>

            <div className="lnat-qcard-panel">
              <span className="lnat-mono-tag">Feedback</span>
              <p>The thesis is clear and confidently stated, but the argument relies on a slippery-slope claim — "opens the door to broader censorship" — without evidence to support it. Strengthen this by citing a specific case or example.</p>
              <p>The essay does not engage with any counterargument. A stronger answer would address a situation where harm might reasonably justify limiting speech, then explain why the essay's position still holds.</p>
              <p>Language is clear and grammatically confident throughout, but the absolute framing — "always," "regardless" — closes off nuance the LNAT rewards. Softer, more precise claims tend to score higher than sweeping ones.</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="lnat-editorial-section">
        <div className="lnat-container">
          <div className="lnat-editorial-section-head">
            <span className="lnat-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools</h2>
            <p>Deployed across top UK grammar and independent schools with LNAT cohorts</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="lnat-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="lnat-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="lnat-school-more">
                  Find out more about{' '}
                  <button type="button" className="lnat-inline-link lnat-link-btn" onClick={() => navigate('/cases')}>
                    {school.name}
                  </button>
                </p>
                <div className="lnat-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="lnat-editorial-section lnat-pricing-editorial">
        <div className="lnat-container">
          <div className="lnat-editorial-section-head">
            <span className="lnat-mono-eyebrow">Prep Tiers</span>
            <h2>Select Your Tier</h2>
            <p>Choose your level and go deeper when you're ready to improve your score</p>
            {!mayPurchase && <p>{ORG_PLAN_NOTICE}</p>}
          </div>

          <div className="lnat-pricing-grid">
            {(EXAM_PACKAGES.find(p => p.id === 'lnat')?.plans || []).map((plan) => {
              const isFeatured = plan.tierKey === 'lnat_beyond';
              const label = plan.tierKey === 'lnat_beginnings' ? 'Beginnings' : (plan.tierKey === 'lnat_foundations' ? 'Foundations' : 'Beyond');
              return (
                <div key={plan.tierKey} className={`lnat-plan ${isFeatured ? 'featured' : ''}`}>
                  {isFeatured && <span className="lnat-plan-badge">Recommended · Oxbridge</span>}
                  <h3>{label}</h3>
                  <div className="lnat-plan-price">
                    <span className="amount">{plan.price}</span>{' '}
                    <span className="period">{plan.period}</span>
                  </div>
                  {(mayPurchase || plan.isFree) && (
                    <button type="button" className="lnat-plan-btn" onClick={() => handleSubscribe(plan.name)}>
                      Get Started
                    </button>
                  )}
                  <ul>
                    {plan.features.map((feat, idx) => (
                      <li key={idx}>
                        <span className="lnat-check">·</span>{' '}
                        {/^All content in /i.test(feat) ? <b>{feat}</b> : feat}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="lnat-editorial-section lnat-faq-editorial">
        <div className="lnat-container">
          <div className="lnat-editorial-section-head">
            <span className="lnat-mono-eyebrow">Got Questions?</span>
            <h2>FAQs</h2>
            <p>Find answers on prep tiers, scoring and how IVORI's feedback works</p>
          </div>

          <div className="lnat-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`lnat-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="lnat-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="lnat-faq-plus">+</span>
                </div>
                <div className="lnat-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LnatEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}